import React, { useRef, useState, useEffect } from "react";
import toast from "react-hot-toast";
import Card from "components/card";
import { uploadImage } from "utils/api";

const EditServiceModal = ({ service, onCancel, onSave }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState(service?.name ?? "");
  const [banner_img, setBannerImg] = useState(service?.banner_img ?? "");

  useEffect(() => {
    setName(service?.name ?? "");
    setBannerImg(service?.banner_img ?? "");
  }, [service]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file (e.g. JPG, PNG)");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadImage(file, "services");
      const url = res.data?.url;
      if (url) {
        setBannerImg(url);
        toast.success("Image uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      console.error(err);
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d?.msg;
      toast.error(msg || "Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = () => {
    if (!name?.trim()) {
      toast.error("Please enter a service name");
      return;
    }
    onSave({ name: name.trim(), banner_img });
  };

  if (!service) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <Card extra={"w-full max-w-lg mx-4 px-0 py-0 shadow-2xl"}>
        <div className="bg-gradient-to-r from-brand-500 to-brand-600 px-6 py-6 rounded-t-2xl flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-white">Edit Service</h3>
            <p className="text-brand-100 text-sm mt-1">Update name and banner image</p>
          </div>
          <button
            onClick={onCancel}
            className="text-white hover:text-brand-100 text-3xl font-light hover:scale-125 transition-transform"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-8 space-y-6">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-300 font-bold uppercase tracking-wider block mb-2">
              Service Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Wash & Fold"
              className="w-full px-4 py-3 border-2 border-gray-200 dark:border-white/10 rounded-lg text-navy-700 dark:text-white dark:bg-navy-800 focus:outline-none focus:border-brand-500 dark:focus:border-brand-400 transition-colors bg-white dark:bg-white/5"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600 dark:text-gray-300 font-bold uppercase tracking-wider block mb-2">
              Banner Image
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            {banner_img ? (
              <div className="relative rounded-lg border-2 border-gray-200 dark:border-white/10 overflow-hidden bg-gray-100 dark:bg-navy-800">
                <img
                  src={banner_img}
                  alt="Service banner"
                  className="w-full h-40 object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="px-3 py-1.5 bg-white/90 text-navy-700 rounded-md text-sm font-semibold disabled:opacity-50"
                  >
                    {uploading ? "Uploading…" : "Change"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBannerImg("")}
                    className="px-3 py-1.5 bg-red-500 text-white rounded-md text-sm font-semibold"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full px-4 py-6 border-2 border-dashed border-gray-300 dark:border-white/20 rounded-lg text-gray-500 dark:text-gray-400 hover:border-brand-500 hover:text-brand-600 dark:hover:text-brand-400 transition-colors flex flex-col items-center gap-2"
              >
                {uploading ? (
                  <span className="font-medium">Uploading…</span>
                ) : (
                  <>
                    <span className="text-2xl">📷</span>
                    <span className="font-medium">Choose image (optional)</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-white/5 border-t border-gray-200 dark:border-white/10 rounded-b-2xl flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-white/20 text-navy-700 dark:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
          >
            Save changes
          </button>
        </div>
      </Card>
    </div>
  );
};

export default EditServiceModal;
